import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import IconMinus from "../icons/iconMinus";
import IconPlus from "../icons/iconPlus";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import { RequiredIndicator } from "./requiredIndicator";

// Simplified props interface - no react-hook-form integration
interface NumberInputProps {
  id: string;
  name: string;
  label?: string;
  min?: number;
  max?: number;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  error?: { message?: string };
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaDescribedBy?: string;
  bookingTypeName?: string;
  venueName?: string;
}

// Helper function to validate and adjust value within bounds
const validateAndAdjustValue = (value: string, min: number, max?: number): string => {
  // If empty, return empty
  if (!value?.trim()) {
    return "";
  }

  // Parse as number
  const numValue = Number(value);

  // Adjust to min if below
  if (numValue < min) {
    return min.toString();
  }

  // Adjust to max if above
  if (max !== undefined && numValue > max) {
    return max.toString();
  }

  return value;
};

// Helper function to sanitize input - only allow digits
const sanitizeInput = (value: string): string => {
  // Only allow digits (0-9), remove everything else including special characters and minus signs
  return value.replace(/\D/g, "");
};

const NumberInput: React.FC<NumberInputProps> = ({
  id,
  name,
  label,
  min = 0,
  max,
  required = false,
  className = "gcw:w-full",
  labelClassName = "",
  inputClassName = "",
  error,
  value,
  onChange,
  disabled = false,
  ariaDescribedBy,
  bookingTypeName = "",
  venueName = "",
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const inputRef = useRef<HTMLInputElement>(null);

  // Track validation state for boundary messages
  const [validationState, setValidationState] = useState({
    showMinMessage: false,
    showMaxMessage: false,
  });

  // Update validation state based on current value
  const updateValidationState = useCallback(
    (currentValue: string) => {
      if (!currentValue?.trim()) {
        setValidationState({ showMinMessage: false, showMaxMessage: false });
        return;
      }

      const numValue = Number(currentValue);
      const isBelowMin = numValue < min;
      const isAtMin = numValue === min;
      const isAtMax = max !== undefined && numValue >= max;

      setValidationState({
        showMinMessage: Boolean((isBelowMin || (isAtMin && !isAtMax)) && bookingTypeName && venueName),
        showMaxMessage: Boolean(isAtMax && bookingTypeName && venueName),
      });
    },
    [min, max, bookingTypeName, venueName],
  );

  // Update validation state when input value changes
  useEffect(() => {
    updateValidationState(value);
  }, [value, updateValidationState]);

  // Check if current value is at boundaries
  const checkBoundaries = useCallback(
    (currentValue: string) => {
      if (!currentValue?.trim()) {
        return { isAtMin: false, isAtMax: false };
      }

      const numValue = Number(currentValue);
      const isAtMin = numValue <= min;
      const isAtMax = max !== undefined && numValue >= max;

      return { isAtMin, isAtMax };
    },
    [min, max],
  );

  // Handle increment button click
  const handleIncrement = useCallback(() => {
    if (disabled) return;

    const current = value ?? "0";
    const numValue = Number(current);
    const newValue = (numValue + 1).toString();

    // Adjust to max if needed
    const adjustedValue = validateAndAdjustValue(newValue, min, max);
    onChange(adjustedValue);
  }, [disabled, value, min, max, onChange]);

  // Handle decrement button click
  const handleDecrement = useCallback(() => {
    if (disabled) return;

    const current = value ?? "0";
    const numValue = Number(current);
    const newValue = (numValue - 1).toString();

    // Adjust to min if needed
    const adjustedValue = validateAndAdjustValue(newValue, min, max);
    onChange(adjustedValue);
  }, [disabled, value, min, max, onChange]);

  // Handle input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      // Sanitize input to only allow digits
      const sanitizedValue = sanitizeInput(rawValue);
      onChange(sanitizedValue);
    },
    [onChange],
  );

  // Handle paste events to sanitize pasted content
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const sanitizedText = sanitizeInput(pastedText);

      // Insert sanitized text at cursor position
      const input = e.currentTarget;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const currentValue = value || "";
      const newValue = `${currentValue.substring(0, start)}${sanitizedText}${currentValue.substring(end)}`;

      onChange(newValue);
    },
    [disabled, value, onChange],
  );

  // Handle input blur - validate and adjust
  const handleInputBlur = useCallback(() => {
    const adjustedValue = validateAndAdjustValue(value, min, max);
    if (adjustedValue !== value) {
      onChange(adjustedValue);
    }
  }, [value, min, max, onChange]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      // Prevent invalid key presses
      const isDigit = /^\d$/.test(e.key);
      const isNavigationKey = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Backspace",
        "Delete",
      ].includes(e.key);

      const isCtrlKey = e.ctrlKey || e.metaKey;
      const isSelectAll = isCtrlKey && e.key === "a";
      const isCopy = isCtrlKey && e.key === "c";
      const isPaste = isCtrlKey && e.key === "v";
      const isCut = isCtrlKey && e.key === "x";

      // Allow: digits, navigation keys, ctrl combinations, backspace, delete
      if (!isDigit && !isNavigationKey && !isSelectAll && !isCopy && !isPaste && !isCut && !isCtrlKey) {
        e.preventDefault();
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          handleDecrement();
          break;
        case "ArrowUp":
          e.preventDefault();
          handleIncrement();
          break;
        case "Enter": {
          e.preventDefault();
          const adjustedValue = validateAndAdjustValue(value, min, max);
          if (adjustedValue !== value) {
            onChange(adjustedValue);
          }
          break;
        }
      }
    },
    [disabled, handleDecrement, handleIncrement, value, min, max, onChange],
  );

  // Check button states
  const { isAtMin, isAtMax } = checkBoundaries(value);
  const { showMinMessage, showMaxMessage } = validationState;

  // Build aria-describedby parts
  const ariaDescribedByParts: string[] = [];
  if (error?.message) ariaDescribedByParts.push(`${id}-error`);
  if (showMinMessage) ariaDescribedByParts.push(`${id}-min-message`);
  if (showMaxMessage) ariaDescribedByParts.push(`${id}-max-message`);
  if (ariaDescribedBy) ariaDescribedByParts.push(ariaDescribedBy);

  const renderLimitMessage = (messageId: string, message: string) => {
    const output = (
      <output
        id={messageId}
        className={isWidget ? "gcw:block" : "gcw:text-body gcw:text-text-secondary gcw:mt-1"}
        aria-live="polite"
      >
        {message}
      </output>
    );

    return isWidget ? (
      <div className="info-alert booking-guests-limit-message gcw:border gcw:rounded-md gcw:text-body">
        <div>{output}</div>
      </div>
    ) : (
      output
    );
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={`gcw:block gcw:mb-2 ${labelClassName}`}>
          {label}
          {required && <RequiredIndicator />}
        </label>
      )}

      <div className="gcw:flex gcw:w-full gcw:items-center gcw:gap-2">
        {/* Minus button */}
        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || isAtMin}
          className="booking-guest-stepper-button gcw:flex gcw:flex-1 gcw:items-center gcw:justify-center gcw:h-10 gcw:rounded-lg gcw:border gcw:border-text-muted gcw:bg-white gcw:hover:bg-gray-50 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed gcw:transition-colors"
          aria-label={t("decrement_guests", "Decrease number of guests")}
          aria-disabled={disabled || isAtMin}
        >
          <IconMinus color={disabled || isAtMin ? "#A7AFB8" : "#1B232A"} />
        </button>

        {/* Number input */}
        <input
          ref={inputRef}
          id={id}
          type="number"
          className={`gcw:flex-1 gcw:h-10 gcw:min-w-0 gcw:text-center container-inputs hide-number-spinner ${inputClassName} ${error ? "gcw:border-error" : ""}`}
          name={name}
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          min={min}
          max={max}
          disabled={disabled}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined}
        />

        {/* Plus button */}
        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || isAtMax}
          className="booking-guest-stepper-button gcw:flex gcw:flex-1 gcw:items-center gcw:justify-center gcw:h-10 gcw:rounded-lg gcw:border gcw:border-text-muted gcw:bg-white gcw:hover:bg-gray-50 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed gcw:transition-colors"
          aria-label={t("increment_guests", "Increase number of guests")}
          aria-disabled={disabled || isAtMax}
        >
          <IconPlus color={disabled || isAtMax ? "#A7AFB8" : "#1B232A"} />
        </button>
      </div>

      {/* Error message */}
      {error?.message && (
        <div id={`${id}-error`} className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
          {error.message}
        </div>
      )}

      {/* Minimum guests message */}
      {showMinMessage &&
        renderLimitMessage(
          `${id}-min-message`,
          t(
            "minimum_guests_reached",
            "You reached the minimum number of guests set for this {{bookingType}}. You can contact {{venueName}} for more information",
            {
              bookingType: bookingTypeName,
              venueName: venueName,
            },
          ),
        )}

      {/* Maximum guests message */}
      {showMaxMessage &&
        renderLimitMessage(
          `${id}-max-message`,
          t(
            "maximum_guests_reached",
            "You reached the maximum number of guests for this {{bookingType}}. You can contact {{venueName}} for more information",
            {
              bookingType: bookingTypeName,
              venueName: venueName,
            },
          ),
        )}
    </div>
  );
};

export default NumberInput;
